import { useState, forwardRef, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useQuery } from '@tanstack/react-query';
import { InviteModal } from '@/components/InviteModal';
import { useInviteToken } from '@/hooks/useInviteToken';
import { LeaveChallenge } from '@/components/LeaveChallenge';
import { PastChallenges } from '@/components/PastChallenges';
import { GroupMembers } from '@/components/GroupMembers';
import { calculateDaysRemaining } from '@/lib/utils';
import { usePersonalStreak } from '@/hooks/usePersonalStreak';

interface ProfileSectionProps {
  targetUserId?: string;
  onModalOpen?: () => void;
  onModalClose?: () => void;
}

export const ProfileSection = forwardRef<HTMLElement, ProfileSectionProps>(
  function ProfileSection({ targetUserId, onModalOpen, onModalClose }, ref) {
    const { user, profile: currentUserProfile, group, membership } = useAuth();
    const { t, language } = useLanguage();
    const [showInviteModal, setShowInviteModal] = useState(false);
    const { token: inviteToken } = useInviteToken();
    
    const isOwnProfile = !targetUserId || targetUserId === user?.id;
    const effectiveUserId = targetUserId || user?.id;

    // Notify parent of modal state
    useEffect(() => {
      if (showInviteModal) {
        onModalOpen?.();
      } else {
        onModalClose?.();
      }
    }, [showInviteModal, onModalOpen, onModalClose]);

    // Get personal streak data (independent of challenge)
    const { data: personalStreakData } = usePersonalStreak();

    // Fetch profile (use secure function for other users' profiles to avoid email exposure)
    const { data: profile } = useQuery({
      queryKey: ['profile', effectiveUserId, isOwnProfile, group?.id],
      queryFn: async () => {
        if (!effectiveUserId) return null;
        
        if (isOwnProfile) {
          const { data } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', effectiveUserId)
            .maybeSingle();
          return data;
        } else {
          if (!group) return null;
          const { data: profiles } = await supabase
            .rpc('get_group_member_profiles', { target_group_id: group.id });
          return profiles?.find(p => p.id === effectiveUserId) || null;
        }
      },
      enabled: !!effectiveUserId && (isOwnProfile || !!group),
    });

    // Fetch drink logs for challenge stats (only if in active challenge)
    const { data: challengeDrinkLogs } = useQuery({
      queryKey: ['challenge-drink-logs', effectiveUserId, group?.id],
      queryFn: async () => {
        if (!effectiveUserId || !group) return [];
        const { data } = await supabase
          .from('drink_logs')
          .select('*')
          .eq('user_id', effectiveUserId)
          .eq('group_id', group.id)
          .order('logged_at', { ascending: false });
        return data || [];
      },
      enabled: !!effectiveUserId && !!group,
    });

    // Fetch ALL drink logs for personal stats (for last drink display)
    const { data: allDrinkLogs } = useQuery({
      queryKey: ['all-drink-logs', effectiveUserId],
      queryFn: async () => {
        if (!effectiveUserId) return [];
        const { data } = await supabase
          .from('drink_logs')
          .select('*')
          .eq('user_id', effectiveUserId)
          .order('logged_at', { ascending: false });
        return data || [];
      },
      enabled: !!effectiveUserId && isOwnProfile,
    });

    // Challenge-specific stats (only when in active challenge)
    const hasActiveChallenge = group && 
      group.status === 'active' && 
      new Date(group.challenge_end) > new Date();

    const challengeTotalDrinks = challengeDrinkLogs?.length || 0;
    const remainingDrinks = Math.max(0, 100 - challengeTotalDrinks);
    
    const beerCount = challengeDrinkLogs?.filter(d => d.drink_type === 'beer').length || 0;
    const wineCount = challengeDrinkLogs?.filter(d => d.drink_type === 'wine').length || 0;
    const shotCount = challengeDrinkLogs?.filter(d => d.drink_type === 'shot').length || 0;

    // Calculate days remaining using shared utility (challenge-specific)
    const daysRemaining = group ? calculateDaysRemaining(group.challenge_end) : 0;

    // Calculate weekends remaining (rough estimate)
    const weekendsRemaining = Math.ceil(daysRemaining / 7);
    const drinksPerWeekend = weekendsRemaining > 0 ? (remainingDrinks / weekendsRemaining).toFixed(1) : 0;

    // Personal streak (global, independent of challenge)
    const personalCurrentStreak = personalStreakData?.personalCurrentStreak ?? 0;
    const personalLongestStreak = personalStreakData?.personalLongestStreak ?? 0;

    // Calculate monthly breakdown for challenge
    const monthlyStats = () => {
      if (!challengeDrinkLogs || challengeDrinkLogs.length === 0) return [];
      
      const stats = new Map<string, { beer: number; wine: number; shot: number }>();
      
      for (const log of challengeDrinkLogs) {
        const date = new Date(log.logged_at);
        const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        
        if (!stats.has(monthKey)) {
          stats.set(monthKey, { beer: 0, wine: 0, shot: 0 });
        }
        
        const entry = stats.get(monthKey)!;
        if (log.drink_type === 'beer') entry.beer++;
        else if (log.drink_type === 'wine') entry.wine++;
        else if (log.drink_type === 'shot') entry.shot++;
      }
      
      return Array.from(stats.entries())
        .sort((a, b) => b[0].localeCompare(a[0]))
        .map(([month, counts]) => ({
          month: new Date(month + '-01').toLocaleDateString(language === 'sk' ? 'sk-SK' : 'en-US', { 
            month: 'long', 
            year: 'numeric' 
          }),
          ...counts,
          total: counts.beer + counts.wine + counts.shot,
        }));
    };

    const monthly = monthlyStats();

    const displayProfile = isOwnProfile ? currentUserProfile : profile;

    // Get last drink from all logs (personal timeline)
    const lastDrinkLog = isOwnProfile ? allDrinkLogs?.[0] : challengeDrinkLogs?.[0];

    return (
      <section ref={ref} id="profile" className="py-8 border-t border-border">
        {/* Section Header */}
        <div className="px-6 pb-4 text-center max-w-lg mx-auto">
          <h2 className="text-xl font-serif font-bold">
            {displayProfile?.user_id || t('profileTitle')}
          </h2>
        </div>

        <div className="px-6 max-w-lg mx-auto space-y-6">
          {/* Challenge Progress - only show when in active challenge */}
          {hasActiveChallenge && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('profileChallengeProgress')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <div className="text-3xl font-bold text-primary">{challengeTotalDrinks}</div>
                    <div className="text-sm text-muted-foreground">{t('profileDrinksLogged')}</div>
                  </div>
                  <div className="text-center p-4 bg-muted/30 rounded-lg">
                    <div className="text-3xl font-bold">{remainingDrinks}</div>
                    <div className="text-sm text-muted-foreground">{t('profileRemaining')}</div>
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                  <div className="p-2">
                    <div className="text-2xl">🍺</div>
                    <div className="font-bold">{beerCount}</div>
                  </div>
                  <div className="p-2">
                    <div className="text-2xl">🍷</div>
                    <div className="font-bold">{wineCount}</div>
                  </div>
                  <div className="p-2">
                    <div className="text-2xl">🥃</div>
                    <div className="font-bold">{shotCount}</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Personal Stats - always show for own profile */}
          {isOwnProfile && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t('profilePersonalStats')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {hasActiveChallenge && (
                  <p className="text-sm text-muted-foreground">
                    {daysRemaining} {t('profileDaysRemaining')}
                  </p>
                )}
                <div className="text-sm">
                  <span className="text-muted-foreground">{t('profileLastDrink')} </span>
                  <span className="font-medium">
                    {lastDrinkLog
                      ? new Date(lastDrinkLog.logged_at).toLocaleString(language === 'sk' ? 'sk-SK' : 'en-GB', {
                          day: 'numeric',
                          month: 'numeric',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        }).replace(',', ' at')
                      : t('profileNoLastDrink')}
                  </span>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">{t('profileCurrentStreak')} </span>
                  <span className="font-medium">{personalCurrentStreak} {t('profileDaysWithoutAlcohol')}</span>
                </div>
                <div className="text-sm">
                  <span className="text-muted-foreground">{t('profileLongestStreak')} </span>
                  <span className="font-medium">{personalLongestStreak} {t('profileDaysWithoutAlcohol')}</span>
                </div>
                {hasActiveChallenge && (
                  <div className="pt-2 border-t border-border">
                    <p className="text-sm text-muted-foreground">
                      {t('profileWeekendStats')} <strong>{weekendsRemaining} {t('profileWeekends')}</strong>, {t('profileYouCanDrink')}{' '}
                      <strong>{remainingDrinks} {t('profileDrinks')}</strong> {t('profileThatsAbout')}{' '}
                      <strong>{drinksPerWeekend} {t('profileDrinksPerWeekend')}</strong>.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Privacy notice for other users' profiles */}
          {!isOwnProfile && (
            <Card>
              <CardContent className="py-4">
                <p className="text-sm text-muted-foreground text-center">
                  {t('profilePrivacyNotice')}
                </p>
              </CardContent>
            </Card>
          )}

          {/* Monthly Breakdown - only show when in active challenge with data */}
          {hasActiveChallenge && monthly.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">{t('profileMonthlyOverview')}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-1">
                  {/* Header */}
                  <div className="grid grid-cols-[1fr_repeat(4,_40px)] gap-2 text-xs text-muted-foreground font-medium pb-2 border-b border-border">
                    <span>{t('profileMonth')}</span>
                    <span className="text-center">🍺</span>
                    <span className="text-center">🍷</span>
                    <span className="text-center">🥃</span>
                    <span className="text-center">Σ</span>
                  </div>

                  {/* Rows */}
                  {monthly.map((row) => (
                    <div 
                      key={row.month}
                      className="grid grid-cols-[1fr_repeat(4,_40px)] gap-2 py-2"
                    >
                      <span className="text-sm">{row.month}</span>
                      <span className="text-center text-sm">{row.beer}</span>
                      <span className="text-center text-sm">{row.wine}</span>
                      <span className="text-center text-sm">{row.shot}</span>
                      <span className="text-center text-sm font-bold">{row.total}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Challenge Membership - show invite token for all members */}
          {isOwnProfile && hasActiveChallenge && membership && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg">{t('profileChallengeMembership')}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('profileRole')}</span>
                  <span className="text-sm font-medium capitalize">{membership.role}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{t('leaderboardInviteCode')}</span>
                  <button
                    onClick={() => inviteToken && setShowInviteModal(true)}
                    className="font-mono font-bold text-primary underline underline-offset-2 decoration-primary/50 hover:decoration-primary transition-colors"
                  >
                    {inviteToken || '···'}
                  </button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Group Members - only show on own profile for creators */}
          {isOwnProfile && hasActiveChallenge && <GroupMembers />}

          {/* Leave Challenge - only show on own profile when in active challenge */}
          {isOwnProfile && hasActiveChallenge && <LeaveChallenge onInviteClick={() => setShowInviteModal(true)} />}

          {/* Past Challenges - only show on own profile */}
          {isOwnProfile && <PastChallenges />}
        </div>

        {/* Invite Modal */}
        {showInviteModal && inviteToken && (
          <InviteModal 
            token={inviteToken} 
            onClose={() => setShowInviteModal(false)} 
          />
        )}
      </section>
    );
  }
);
